// components/ProjectIntakePanel.jsx
// Created: 2026-07-20
// Project: Giving Words to Voices
// Author: Michael Schwarz
// Version: 1.0.0
//
// Registering an interview — the one place a project is created.
//
// Deliberately NOT called "New Project" in the body copy: it cannot create a
// folder. Media arrives on the archive by other routes, and the NAS is
// NFS-mounted with root-squash, so the backend cannot reliably make directories
// there. This registers a folder that already exists and records who is in it.
//
// Four required fields, not fourteen. A long intake form gets abandoned or
// filled with placeholder text, and junk metadata is worse than missing
// metadata because people trust it. Everything else is editable afterwards in
// the Subject screen, which exists for exactly that.

import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Check, Film, FolderOpen, Loader, RefreshCw, UserPlus } from 'lucide-react';
import { Icon } from './Icon';
import LoadFailure from './LoadFailure';
import Button from './ui/Button';
import Input from './ui/Input';
import Select from './ui/Select';

export default function ProjectIntakePanel({ theme, axiosAuth, pushToast = () => {}, onCountChange = () => {} }) {
  const [candidates, setCandidates] = useState([]);
  const [takenRoots, setTakenRoots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [folder, setFolder] = useState('');
  const [inspection, setInspection] = useState(null);
  const [saving, setSaving] = useState(false);
  const [ack, setAck] = useState(false);
  const [lastRegistered, setLastRegistered] = useState(null);

  const [form, setForm] = useState({
    interviewee_name: '', interview_language: '', interviewer_name: '',
    project_root: '', interview_date: '', interview_place: '', notes: '',
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Languages the archive already uses, so the list reflects this collection
  // rather than a guess. Falls back to a sensible set if the call fails —
  // an empty dropdown would block intake entirely.
  const [languages, setLanguages] = useState(
    ['Swedish', 'English', 'Hebrew', 'Polish', 'German', 'Yiddish', 'Russian', 'Hungarian']);
  useEffect(() => {
    axiosAuth.get('/pipeline/languages')
      .then(r => {
        const all = [...new Set([...(r.data?.used || []), ...(r.data?.all || [])])].filter(Boolean);
        if (all.length) setLanguages(all);
      })
      .catch(() => {});
  }, [axiosAuth]);

  const loadCandidates = useCallback(() => {
    setLoading(true);
    axiosAuth.get('/intake/candidates')
      .then(r => {
        const list = r.data?.candidates || [];
        setCandidates(list);
        setTakenRoots(r.data?.taken_roots || []);
        onCountChange(list.length);
        setLoadError(false);
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, [axiosAuth, onCountChange]);

  useEffect(() => { loadCandidates(); }, [loadCandidates]);

  // Inspect on selection: what is in the folder, and does it duplicate anything.
  useEffect(() => {
    if (!folder) { setInspection(null); setAck(false); return; }
    setAck(false);
    axiosAuth.get(`/intake/inspect?folder=${encodeURIComponent(folder)}`)
      .then(r => {
        setInspection(r.data);
        // Pre-fill the name from what the files suggest, so the common case is
        // a glance and a confirm rather than retyping.
        const suggested = (candidates.find(c => c.folder === folder) || {}).suggested_root || '';
        setForm(f => ({ ...f, interviewee_name: f.interviewee_name || suggested }));
      })
      .catch(() => setInspection(null));
  }, [folder, axiosAuth, candidates]);

  const duplicates = inspection?.possible_duplicates || [];
  const blockedOnDuplicate = duplicates.length > 0 && !ack;

  // Two people can share a name, and one person can be interviewed twice. The
  // interviewee's name is what everyone reads; the project name is the key
  // underneath it and has to be unique. Surfaced only when they collide, so
  // the usual case stays a four-field form — but surfaced BEFORE submitting,
  // because bouncing an admin off "choose a different project name" with no
  // field to change is a dead end.
  const nameTaken = takenRoots.some(
    r => r.toLowerCase() === form.interviewee_name.trim().toLowerCase());
  const suggestUniqueRoot = useCallback((base) => {
    let n = 2;
    let candidate = `${base} (${n})`;
    while (takenRoots.some(r => r.toLowerCase() === candidate.toLowerCase())) {
      n += 1;
      candidate = `${base} (${n})`;
    }
    return candidate;
  }, [takenRoots]);

  useEffect(() => {
    if (!nameTaken) return;
    setForm(f => f.project_root
      ? f
      : { ...f, project_root: suggestUniqueRoot(f.interviewee_name.trim()) });
  }, [nameTaken, suggestUniqueRoot]);

  const rootConflict = nameTaken && takenRoots.some(
    r => r.toLowerCase() === (form.project_root || '').trim().toLowerCase());

  const missing = !folder || !form.interviewee_name.trim()
                  || !form.interview_language.trim() || !form.interviewer_name.trim()
                  || (nameTaken && !form.project_root.trim());

  const submit = () => {
    if (missing || blockedOnDuplicate || rootConflict || saving) return;
    setSaving(true);
    axiosAuth.post('/intake', { folder, ...form, acknowledge_duplicate: ack })
      .then(r => {
        // Say what happens next. Registering is not the end of anything — the
        // interview now exists but has nothing to read until it is transcribed,
        // and without saying so the form just goes quiet.
        const media = inspection?.media_files || 0;
        const subs = inspection?.subtitle_files || 0;
        pushToast(`intake-${Date.now()}`, 'success',
          subs > 0
            ? `${r.data.display_name} registered. It already has transcripts — open it from the Dashboard.`
            : `${r.data.display_name} registered with ${media} media file${media === 1 ? '' : 's'} `
              + 'and no transcript yet. Transcribe it to begin; speakers will be named automatically.');
        setLastRegistered({ name: r.data.display_name, folder: r.data.folder, media, subs });
        setFolder('');
        setForm({ interviewee_name: '', interview_language: '', interviewer_name: '',
                  project_root: '', interview_date: '', interview_place: '', notes: '' });
        setInspection(null);
        loadCandidates();
      })
      .catch(e => pushToast('intake-error', 'error',
        e?.response?.data?.detail || 'Could not register this interview'))
      .finally(() => setSaving(false));
  };

  const label = (text, required) => (
    <div style={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.07em',
                  textTransform: 'uppercase', color: theme.textSub, marginBottom: '5px' }}>
      {text}{required && <span style={{ color: theme.accent || '#ef4444' }}> *</span>}
    </div>
  );

  if (loading) {
    return <div style={{ padding: '30px', textAlign: 'center', color: theme.textSub }}>
      <Icon icon={Loader} size={16} style={{ animation: 'spin 1s linear infinite' }} /> Reading the archive…
    </div>;
  }
  if (loadError) {
    return <LoadFailure theme={theme} what="the list of folders" onRetry={loadCandidates} />;
  }

  return (
    <div style={{ maxWidth: '720px' }}>
      <p style={{ fontSize: '0.8rem', color: theme.textSub, lineHeight: 1.55, marginTop: 0 }}>
        Registers a folder that is already on the archive as an interview. It does not
        create folders — copy the media into place first, then register it here.
      </p>

      {lastRegistered && (
        <div style={{ display: 'flex', gap: '9px', alignItems: 'flex-start', padding: '12px 14px',
                      borderRadius: '9px', marginBottom: '14px',
                      background: '#22c55e14', border: '1px solid #22c55e55' }}>
          <Icon icon={Check} size={14} style={{ color: '#22c55e', marginTop: '1px' }} />
          <div style={{ fontSize: '0.76rem', color: theme.text, lineHeight: 1.5 }}>
            <strong>{lastRegistered.name}</strong> is registered from <code>{lastRegistered.folder}</code>.
            {lastRegistered.subs > 0
              ? ' It already has transcripts, so it is ready to work on from the Dashboard.'
              : ` It has ${lastRegistered.media} media file${lastRegistered.media === 1 ? '' : 's'} and no transcript yet.`}
            {lastRegistered.subs === 0 && (
              <div style={{ marginTop: '5px', color: theme.textSub }}>
                Next: transcribe it. Speakers will be named from the interviewee and
                interviewer you just entered, with no further input.
              </div>
            )}
          </div>
        </div>
      )}

      {candidates.length === 0 ? (
        <div style={{ padding: '22px', textAlign: 'center', border: `1px dashed ${theme.border}`,
                      borderRadius: '10px', color: theme.textSub, fontSize: '0.8rem' }}>
          <Icon icon={Check} size={16} style={{ color: '#22c55e' }} />
          <div style={{ marginTop: '8px' }}>Every folder on the archive is already a registered interview.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

          {/* Folder — a picker, never free text. A typed path is how you get a
              project pointing at nothing. */}
          <div>
            {label('Folder', true)}
            <div style={{ display: 'flex', gap: '8px' }}>
              <Select theme={theme} value={folder} onChange={e => setFolder(e.target.value)} style={{ flex: 1 }}>
                <option value="">Choose a folder…</option>
                {candidates.map(c => (
                  <option key={c.folder} value={c.folder}>
                    {c.folder}{c.media_files ? ` — ${c.media_files} media file${c.media_files === 1 ? '' : 's'}` : ' — no media'}
                    {c.status === 'unconfirmed' ? ' (awaiting intake)' : ''}
                  </option>
                ))}
              </Select>
              <Button theme={theme} variant="outline" onClick={loadCandidates}
                title="Re-read the archive" icon={RefreshCw} />
            </div>
          </div>

          {inspection && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.72rem',
                            color: theme.textSub, flexWrap: 'wrap' }}>
                <Icon icon={Film} size={12} />
                {inspection.media_files} media, {inspection.subtitle_files} subtitle file
                {inspection.subtitle_files === 1 ? '' : 's'}
              </div>

              {(inspection.warnings || []).map((w, i) => {
                const isDup = duplicates.length > 0 && i === (inspection.warnings.length - 1);
                return (
                  <div key={i} role="alert" style={{
                    display: 'flex', gap: '8px', alignItems: 'flex-start', padding: '10px 12px',
                    borderRadius: '8px', background: '#f59e0b14', border: '1px solid #f59e0b55',
                    fontSize: '0.74rem', color: theme.text, lineHeight: 1.5,
                  }}>
                    <Icon icon={AlertTriangle} size={13} style={{ color: '#f59e0b', marginTop: '1px' }} />
                    <div style={{ flex: 1 }}>
                      {w}
                      {isDup && (
                        <label style={{ display: 'flex', alignItems: 'center', gap: '7px',
                                        marginTop: '8px', cursor: 'pointer', fontWeight: 600 }}>
                          <input type="checkbox" checked={ack} onChange={e => setAck(e.target.checked)} />
                          Yes, this is a separate interview
                        </label>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div>
            {label('Interviewee', true)}
            <Input theme={theme} value={form.interviewee_name} placeholder="The person being interviewed"
                   onChange={e => set('interviewee_name', e.target.value)} />
            <div style={{ fontSize: '0.66rem', color: theme.textSub, marginTop: '4px' }}>
              Becomes the interview's name everywhere in the app.
            </div>
          </div>

          {nameTaken && (
            <div style={{ padding: '12px', borderRadius: '8px',
                          background: '#f59e0b14', border: '1px solid #f59e0b55' }}>
              {label('Project name', true)}
              <Input theme={theme} invalid={rootConflict}
                     value={form.project_root}
                     onChange={e => set('project_root', e.target.value)} />
              <div style={{ fontSize: '0.7rem', color: theme.text, marginTop: '6px', lineHeight: 1.5 }}>
                An interview called <strong>{form.interviewee_name.trim()}</strong> already exists.
                Two people can share a name, and one person can be interviewed twice — so this
                needs its own project name. It is only used internally;
                “{form.interviewee_name.trim()}” is still what you will see in the app.
              </div>
              {rootConflict && (
                <div style={{ fontSize: '0.72rem', color: '#ef4444', marginTop: '6px', fontWeight: 600 }}>
                  That project name is taken too. Try something else.
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 220px' }}>
              {label('Interview language', true)}
              <Select theme={theme} value={form.interview_language}
                      onChange={e => set('interview_language', e.target.value)}>
                <option value="">Choose…</option>
                {languages.map(l => <option key={l} value={l}>{l}</option>)}
              </Select>
            </div>
            <div style={{ flex: '1 1 220px' }}>
              {label('Interviewer', true)}
              <Input theme={theme} value={form.interviewer_name} placeholder="Who conducted the interview"
                     onChange={e => set('interviewer_name', e.target.value)} />
              <div style={{ fontSize: '0.66rem', color: theme.textSub, marginTop: '4px' }}>
                Lets the speaker detection name both voices on its own.
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 200px' }}>
              {label('Interview date')}
              <Input theme={theme} value={form.interview_date} placeholder="1997-03-12, or c. 1997"
                     onChange={e => set('interview_date', e.target.value)} />
            </div>
            <div style={{ flex: '1 1 200px' }}>
              {label('Where it was recorded')}
              <Input theme={theme} value={form.interview_place} placeholder="Stockholm, Sweden"
                     onChange={e => set('interview_place', e.target.value)} />
            </div>
          </div>

          <div>
            {label('Notes')}
            <Input theme={theme} multiline style={{ minHeight: '68px' }} value={form.notes}
                   placeholder="Anything worth knowing before someone starts work on this"
                   onChange={e => set('notes', e.target.value)} />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <button onClick={submit} disabled={missing || blockedOnDuplicate || rootConflict || saving}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '7px',
                padding: '10px 18px', minHeight: '40px', borderRadius: '8px', border: 'none',
                background: (missing || blockedOnDuplicate) ? theme.border : (theme.glossaryAccent || '#2563eb'),
                color: (missing || blockedOnDuplicate) ? theme.textSub : '#fff',
                fontWeight: 700, fontSize: '0.8rem', fontFamily: 'inherit',
                cursor: (missing || blockedOnDuplicate || saving) ? 'not-allowed' : 'pointer',
              }}>
              <Icon icon={saving ? Loader : UserPlus} size={13}
                    style={saving ? { animation: 'spin 1s linear infinite' } : undefined} />
              {saving ? 'Registering…' : 'Register interview'}
            </button>
            {/* Say WHY it is disabled. A greyed button with no reason is a dead end. */}
            {(missing || blockedOnDuplicate) && (
              <span style={{ fontSize: '0.72rem', color: theme.textSub }}>
                {blockedOnDuplicate
                  ? 'Confirm above that this is a separate interview.'
                  : (nameTaken && !form.project_root.trim())
                    ? 'Give this interview its own project name.'
                    : 'Folder, interviewee, language and interviewer are all needed.'}
              </span>
            )}
          </div>
        </div>
      )}

      <div style={{ marginTop: '20px', paddingTop: '14px', borderTop: `1px solid ${theme.border}`,
                    fontSize: '0.7rem', color: theme.textSub, lineHeight: 1.55 }}>
        <Icon icon={FolderOpen} size={11} /> Folders stay invisible to the rest of the app until
        they are registered here. That is deliberate: a folder appearing on the archive means
        something arrived, not that it is an interview.
      </div>
    </div>
  );
}
